import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../services/axios';
import alertService from '../services/alerts';
import defaultPhoto from '../assets/fotoPerfil.jpg'; 
import '../styles/EditarUsuarioPage.css';

function EditarUsuarioPage() {
  const navigate = useNavigate();
  const location = useLocation(); 
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1025);

  const emptyData = {
    nombre: '',
    apellido_paterno: '',
    apellido_materno: '',
    correo: '',
    rol: '',
    imagen: null,
    contrasena: '', 
  };

  const [originalFormData, setOriginalFormData] = useState(null);
  const [previewImage, setPreviewImage] = useState(defaultPhoto);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [formData, setFormData] = useState(emptyData);
  const [initialData, setInitialData] = useState(emptyData);
  const [isFormChanged, setIsFormChanged] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1025;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    if (window.innerWidth >= 1025) {
      setSidebarOpen(true);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (!isEditing) return; 

    const fetchUsuario = async () => {
      try {
        const response = await api.get(`/usuarios/${id}`);
        const u = response.data;

        const userData = {
          nombre: u.nombre || '',
          apellido_paterno: u.apellido_paterno || '',
          apellido_materno: u.apellido_materno || '',
          correo: u.correo || '',
          rol: u.rol || '',
          imagen: null,
          contrasena: '', 
        };

        setFormData(userData);
        setInitialData(userData);

        setOriginalFormData({
          nombre: u.nombre || '',
          apellido_paterno: u.apellido_paterno || '',
          apellido_materno: u.apellido_materno || '',
          correo: u.correo || '',
          rol: u.rol || '',
          imagen: null,
          contrasena: '', 
        });

        const photoURL = await getPhotoUrl(id);
        setPreviewImage(photoURL);
      } catch (err) {
        setError('Error al obtener los datos del usuario');
      }
    };

    fetchUsuario();
  }, [id, isEditing]);

  const getPhotoUrl = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/${userId}/foto`);
      if (!response.ok) return defaultPhoto;

      const blob = await response.blob();
      if (blob.size === 0) return defaultPhoto;

      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('image')) return defaultPhoto;

      return URL.createObjectURL(blob);
    } catch (error) {
      return defaultPhoto;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "image/jpeg") {
        alert("Solo se permiten archivos en formato JPG.");
        return; 
      }
      setFormData((prev) => {
        const updatedData = { ...prev, imagen: file };
        setIsFormChanged(JSON.stringify(updatedData) !== JSON.stringify(initialData));
        return updatedData;
      });
      const previewURL = URL.createObjectURL(file);
      setPreviewImage(previewURL);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };
      setIsFormChanged(JSON.stringify(updatedData) !== JSON.stringify(initialData));
      return updatedData;
    });
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  };

  const validateForm = () => {
    const errors = {};
    const onlyLettersRegex = /^[A-Za-zÀ-ÿ\s]+$/;

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 50) {
      errors.nombre = 'Máximo 50 caracteres';
    } else if (!onlyLettersRegex.test(formData.nombre.trim())) {
      errors.nombre = 'El nombre solo debe contener letras';
    }

    if (!formData.apellido_paterno.trim()) {
      errors.apellido_paterno = 'El apellido paterno es requerido';
    } else if (formData.apellido_paterno.length > 50) {
      errors.apellido_paterno = 'Máximo 50 caracteres';
    }else if (!onlyLettersRegex.test(formData.apellido_paterno.trim())) {
      errors.apellido_paterno = 'El apellido paterno solo debe contener letras';
    }

    if (!formData.apellido_materno.trim()) {
      errors.apellido_materno = 'El apellido materno es requerido';
    } else if (formData.apellido_materno.length > 50) {
      errors.apellido_materno = 'Máximo 50 caracteres';
    } else if (!onlyLettersRegex.test(formData.apellido_materno.trim())) {
      errors.apellido_materno = 'El apellido materno solo debe contener letras';
    }

    if (!formData.correo.trim()) {
      errors.correo = 'El correo es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correo)) {
      errors.correo = 'Formato de correo inválido';
    } else if (formData.correo.length > 100) {
      errors.correo = 'Máximo 100 caracteres';
    }

    if (!formData.rol.trim()) {
      errors.rol = 'El rol es requerido';
    }

    if (!isEditing) {
      if (!formData.contrasena.trim()) {
        errors.contrasena = 'La contraseña es requerida';
      } else if (formData.contrasena.length < 6) {
        errors.contrasena = 'La contraseña debe tener al menos 6 caracteres';
      }
    }

    return errors;
  };

  const checkEmailUnique = async (correo) => {
    try {
      const response = await api.get(`/auth/validate-email?correo=${correo}`);
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  const handleBackButtonClick = async () => {
    if (hasChanges()) {
      const confirmed = await alertService.confirm(
        'Cambios Sin Guardar',
        'Tienes cambios sin guardar. ¿Seguro que deseas regresar?',
        {
          confirmButtonText: 'Sí, Regresar',
          cancelButtonText: 'Cancelar'
        }
      );
      if (confirmed.isConfirmed) {
        navigate('/cuentas');
      }
    } else {
      navigate('/cuentas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    if (!isEditing || (originalFormData && originalFormData.correo !== formData.correo)) {
      const emailExists = await checkEmailUnique(formData.correo);
      if (emailExists) {
        setFormErrors((prev) => ({ ...prev, correo: 'El correo ya está registrado' }));
        return;
      }
    }

    const confirmed = await alertService.confirm(
      isEditing ? 'Guardar Cambios' : 'Crear Usuario',
      isEditing
        ? '¿Estás seguro de guardar los cambios?'
        : '¿Estás seguro de crear este usuario?',
      {
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar'
      }
    );
    if (!confirmed.isConfirmed) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre);
      formDataToSend.append('apellido_paterno', formData.apellido_paterno);
      formDataToSend.append('apellido_materno', formData.apellido_materno);
      formDataToSend.append('correo', formData.correo);
      formDataToSend.append('rol', formData.rol);

      if (!isEditing) {
        formDataToSend.append('contrasena', formData.contrasena);
      }

      if (formData.imagen) {
        formDataToSend.append('imagen', formData.imagen);
      }

      if (isEditing) {
        await api.put(`/usuarios/admin-editar/${id}`, formDataToSend);
        await alertService.success(
          'Usuario Actualizado',
          'Los cambios han sido guardados exitosamente.'
        );
      } else {
        await api.post('/auth/admin-crear', formDataToSend);
        await alertService.success(
          'Usuario Creado',
          'El usuario ha sido creado exitosamente.'
        );
      }

      navigate('/cuentas');
    } catch (err) {
      let errorMessage = 'Error al guardar los cambios';
      
      if (err.response && err.response.status === 409) {
        errorMessage = err.response.data.error || 'Ya existe un usuario con ese correo';
      } else if (err.response && err.response.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (err.response && err.response.data && err.response.data.errors) {
        const serverErrors = {};
        err.response.data.errors.forEach(error => {
          serverErrors[error.param] = error.msg;
        });
        setFormErrors(serverErrors);
        return;
      }
      
      await alertService.error('Error', errorMessage);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasChanges()) {
        const message = 'Tienes cambios sin guardar. ¿Estás seguro de salir?';
        event.returnValue = message;
        return message;
      }
    };
  
    const handleHistoryPopState = (event) => {
      if (hasChanges()) {
        const message = 'Tienes cambios sin guardar. ¿Estás seguro de salir?';
        const userConfirmed = window.confirm(message);
  
        if (userConfirmed) {
          navigate("/cuentas"); 
        } else {
          window.history.pushState(null, null, window.location.href); 
        }
      } else {
        navigate("/cuentas"); 
      }
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handleHistoryPopState);
  
    window.history.pushState(null, null, window.location.href); 
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleHistoryPopState);
    };
  }, [hasChanges(), navigate]);
  

  return (
    <div className="dashboard-container">
      {(!isMobile || (isMobile && sidebarOpen)) && (
        <Sidebar
          onLogoClick={() => {
            if (isMobile) setSidebarOpen(false);
          }}
          onLinkClick={handleCloseSidebar}
        />
      )}

      <div className={`main-content ${isFilterOpen ? 'panel-open' : ''}`} onClick={handleCloseSidebar}>
        <div className="dashboard-title-card">
          {isMobile && (
            <button 
              className="menu-toggle"
              onClick={toggleSidebar}
              aria-label="Abrir menú"
            >
              <i className="fas fa-bars"></i>
            </button>
          )}
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              {isEditing ? 'Editar Usuario' : 'Crear Usuario'}
            </h1>
            <span className="dashboard-title-sub">
              {isEditing ? 'Modificar información del usuario' : 'Registrar nuevo usuario'}
            </span>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

        <div className="futuristic-form-container">
          <div className="form-header">
            <h2 className="form-title">
              <i className="fas fa-user-cog"></i>
              Información del Usuario
            </h2>
            <button className="back-button" onClick={handleBackButtonClick}>
              <i className="fas fa-arrow-left"></i>
              Regresar
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="two-column-form">
              <div className="form-column">
                {isEditing && (
                  <div className="form-group">
                    <label>ID Usuario</label>
                    <input
                      type="text"
                      name="id_usuario"
                      value={id}
                      disabled
                      className="form-input"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Nombre</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Ingresa el nombre"
                    className={`form-input ${formErrors.nombre ? 'error' : ''}`}
                  />
                  {formErrors.nombre && (
                    <span className="error-message">{formErrors.nombre}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Apellido Paterno</label>
                  <input
                    type="text"
                    name="apellido_paterno"
                    value={formData.apellido_paterno}
                    onChange={handleChange}
                    placeholder="Ingresa el apellido paterno"
                    className={`form-input ${formErrors.apellido_paterno ? 'error' : ''}`}
                  />
                  {formErrors.apellido_paterno && (
                    <span className="error-message">{formErrors.apellido_paterno}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Apellido Materno</label>
                  <input
                    type="text"
                    name="apellido_materno"
                    value={formData.apellido_materno}
                    onChange={handleChange}
                    placeholder="Ingresa el apellido materno"
                    className={`form-input ${formErrors.apellido_materno ? 'error' : ''}`}
                  />
                  {formErrors.apellido_materno && (
                    <span className="error-message">{formErrors.apellido_materno}</span>
                  )}
                </div>
              </div>

              <div className="form-column">
                <div className="form-group">
                  <label>Correo</label>
                  <input
                    type="email"
                    name="correo"
                    value={formData.correo}
                    onChange={handleChange}
                    placeholder="Ingresa el correo electrónico"
                    className={`form-input ${formErrors.correo ? 'error' : ''}`}
                  />
                  {formErrors.correo && (
                    <span className="error-message">{formErrors.correo}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Rol</label>
                  <select
                    name="rol"
                    value={formData.rol}
                    onChange={handleChange}
                    className={`select-input ${formErrors.rol ? 'error' : ''}`}
                  >
                    <option value="">Selecciona un rol</option>
                    <option value="administrador">Administrador</option>
                    <option value="lector">Lector</option>
                    <option value="moderador">Moderador</option>
                  </select>
                  {formErrors.rol && (
                    <span className="error-message">{formErrors.rol}</span>
                  )}
                </div>

                {!isEditing && (
                  <div className="form-group">
                    <label>Contraseña</label>
                    <input
                      type="password"
                      name="contrasena"
                      value={formData.contrasena}
                      onChange={handleChange}
                      placeholder="Ingresa la contraseña"
                      className={`form-input ${formErrors.contrasena ? 'error' : ''}`}
                    />
                    {formErrors.contrasena && (
                      <span className="error-message">{formErrors.contrasena}</span>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>Foto de Perfil</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="form-input"
                  />
                  <div className="image-preview">
                    <img
                      src={previewImage}
                      alt="Vista previa"
                      className="preview-image"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="submit-button-container">
              <button 
                type="submit" 
                className="submit-button"
                disabled={!isFormChanged}
              >
                <i className="fas fa-save"></i>
                {isEditing ? 'Guardar Cambios' : 'Crear Usuario'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {isMobile && sidebarOpen && (
        <div className="overlay" onClick={handleCloseSidebar}></div>
      )}
    </div>
  );
}

export default EditarUsuarioPage;
